/**
 * 用户修正偏好（§18.2 / §22.1：对用户经常纠正的同类食物建立个人偏好；
 * §32.3 #8：常见餐食纠正可形成个人偏好）。
 *
 * 实现：以「归一化餐食文本」为键聚合用户修正后的总热量与三大营养素。
 * 请求估算时作为 hints 随提示词发送（见 prompt.ts），**不是**硬编码覆盖——
 * 模型仍按描述与份量估算，用户修正值仅作参考。
 */

import type { MealEntry } from "@jilian/shared";

export interface CorrectionPref {
  /** 归一化餐食文本（去空白、小写） */
  name: string;
  /** 原始描述样例（展示用） */
  rawSample: string;
  portionText: string;
  caloriesMid: number;
  proteinMid: number;
  fatMid: number;
  carbsMid: number;
  /** 统计次数（越多越可信；聚合时加权平均） */
  count: number;
  updatedAt: string;
}

/** 归一化键：去除空白与常见标点（决定性的近似，不解析语言）。 */
export function normalizeMealText(rawText: string): string {
  return rawText
    .trim()
    .toLowerCase()
    .replace(/[，。！？、！？；：、\s,.!?;\n]+/g, "");
}

/** 从一条“已修正”的餐食记录生成（或更新）偏好条目（有既有值时按次数加权取均值）。 */
export function prefFromCorrection(
  entry: Pick<MealEntry, "rawText" | "finalCalories" | "finalProtein" | "finalFat" | "finalCarbs">,
  existing?: CorrectionPref,
): CorrectionPref {
  const name = normalizeMealText(entry.rawText);
  const caloriesMid = entry.finalCalories ?? 0;
  const prev = existing?.count ?? 0;
  const w = (a: number, b: number) => Math.round((prev * a + b) / (prev + 1));
  return {
    name,
    rawSample: entry.rawText.trim(),
    portionText: existing?.portionText ?? "",
    caloriesMid: existing ? w(existing.caloriesMid, caloriesMid) : caloriesMid,
    proteinMid: existing ? w(existing.proteinMid, entry.finalProtein ?? 0) : (entry.finalProtein ?? 0),
    fatMid: existing ? w(existing.fatMid, entry.finalFat ?? 0) : (entry.finalFat ?? 0),
    carbsMid: existing ? w(existing.carbsMid, entry.finalCarbs ?? 0) : (entry.finalCarbs ?? 0),
    count: (existing?.count ?? 0) + 1,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 聚合多条偏好为“最近修正均值”（count 越大权重越高；数量上限由调用方控制）。
 * 结果按 updatedAt 降序（最新的在前），供提示词按序引用。
 */
export function aggregatePrefs(
  prefs: CorrectionPref[],
  maxCount = 10,
): CorrectionPref[] {
  // 同键合并：加权平均（按 count）
  const byName = new Map<string, CorrectionPref & { _w: number }>();
  for (const p of prefs) {
    const prev = byName.get(p.name);
    if (!prev) {
      byName.set(p.name, { ...p, _w: p.count });
      continue;
    }
    const total = prev._w + p.count;
    const w = (a: number, b: number) => (prev._w * a + p.count * b) / total;
    byName.set(p.name, {
      ...prev,
      rawSample: p.rawSample || prev.rawSample,
      portionText: p.portionText || prev.portionText,
      caloriesMid: Math.round(w(prev.caloriesMid, p.caloriesMid)),
      proteinMid: Math.round(w(prev.proteinMid, p.proteinMid)),
      fatMid: Math.round(w(prev.fatMid, p.fatMid)),
      carbsMid: Math.round(w(prev.carbsMid, p.carbsMid)),
      count: total,
      updatedAt: p.updatedAt > prev.updatedAt ? p.updatedAt : prev.updatedAt,
      _w: total,
    });
  }
  return [...byName.values()]
    .map(({ _w: _omit, ...p }) => p)
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, maxCount);
}